/**
 * Web component: Doogma charms (doogma-charms)
 * -----------------------------------------------------------------------------
 * Loads data for products and charms and writes to window variable.
 *
 */

/**
 * Create new <doogma-charms> custom element.
 */
class DoogmaCharms extends HTMLElement {

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super()
    this.charms = {}
    this.maxAllowed = Number(this.dataset.maxCharms)
    this.queryCachePrefix = this.dataset.queryCachePrefix
    this.types = {}
  }

  /**
   * Lifecycle hook - component is attached to the DOM.
   */
  async connectedCallback() {
    sessionStorage.removeItem('doogma-ready')
    await this.loadData()
    this.setButtonState('ready')
  }

  /**
   * Load data and update window variables.
   * @returns {Promise}
   */
  loadData() {
    return new Promise(async(resolve) => {
      this.charms = await this.queryCharms()
      this.types = await this.queryCollections()
      this.updateWindow()

      resolve()
    })
  }

  /**
   * Query charm products.
   * - Used in the data for all products.
   * @returns {Promise}
   */
  queryCharms() {
    return new Promise(async(resolve) => {
      const charmGroups = this.dataset.charmHandles?.split(',')

      const charms = {
        maxAllowed: this.maxAllowed,
      }

      const allCharms = []

      /**
       * Load all charm collections.
       * - TODO: Each collection should be grouped by type.
       * - TODO: Add metafield to collection to control what it represents.
       * - E.g. thread on, welded, clicker.
       */
      for (const handle of charmGroups) {
        const variables = {
          country: this.dataset.country,
          handle,
          language: this.dataset.language?.toUpperCase(),
          metafields: [
            { namespace: 'custom', key: 'engraving_allow_symbols' },
            { namespace: 'custom', key: 'engraving_back_engraving' },
            { namespace: 'custom', key: 'engraving_boundary_preset' },
            { namespace: 'custom', key: 'engraving_bounding_height' },
            { namespace: 'custom', key: 'engraving_bounding_width' },
            { namespace: 'custom', key: 'engraving_max_characters' },
            { namespace: 'custom', key: 'engraving_product_height' },
            { namespace: 'custom', key: 'engraving_product_width' },
          ]
        }

        let products = []
        let after = null
        let hasNextPage = true

        while (hasNextPage) {
          const query = this.getQuery()
          const response = await this.query(query, { ...variables, after })
          const collection = response.data?.collection

          if (!collection) {
            break
          }

          const pageProducts = collection?.products?.edges || []
          const filteredProducts = pageProducts.map(this.formatCharm.bind(this))

          products = products.concat(filteredProducts)
          hasNextPage = collection?.products?.pageInfo?.hasNextPage || false
          after = collection?.products?.pageInfo?.endCursor || null
        }

        allCharms.push(...products)
      }

      /**
       * Sort into charm groups.
       * E.g. Horoscope.
       */
      const uniqueGroups = []

      const groups = [...allCharms]
        .map((item) => {
          return item.charmGroup
        })
        .filter((group) => {
          if (uniqueGroups.includes(group)) {
            return false
          }

          uniqueGroups.push(group)
          return true
        })
        .sort()

      groups.forEach((group) => {
        const items = [...allCharms].filter((charm) => charm.charmGroup === group)

        charms[group] = {
          items,
          title: group
        }
      })

      resolve(charms)
    })
  }

  /**
   * Query collection products.
   * - Used in the data for all products.
   * @returns {Promise}
   */
  queryCollections() {
    return new Promise(async(resolve) => {
      const collectionEntries = this.dataset.collectionHandles?.split(',')
      const types = {}

      const currency = {
        code: this.dataset.currency,
        position: 'before',
        symbol: this.dataset.currencySymbol,
      }

      for (const key of collectionEntries) {
        const type = key.split(':')[0]
        const handle = key.split(':')[1]

        const variables = {
          country: this.dataset.country,
          handle,
          language: this.dataset.language?.toUpperCase(),
          metafields: [],
        }

        let products = []
        let maxAllowed = null
        let after = null
        let hasNextPage = true

        while (hasNextPage) {
          const query = this.getQuery()
          const response = await this.query(query, { ...variables, after })
          const collection = response.data?.collection

          if (!collection) {
            break
          }

          if (collection.maxAllowed && maxAllowed === null) {
            maxAllowed = Number(collection.maxAllowed.value)
          }

          const pageProducts = collection?.products?.edges || []

          const filteredProducts = pageProducts
            .filter(edge => edge.node?.enableCharms?.value === 'true')
            .map(this.formatProduct.bind(this))

          products = products.concat(filteredProducts)
          hasNextPage = collection?.products?.pageInfo?.hasNextPage || false
          after = collection?.products?.pageInfo?.endCursor || null
        }

        /**
         * Filter out unsupported types where product metafield has value.
         */
        let charms = JSON.stringify(this.charms)
        charms = JSON.parse(charms)

        Object.entries(charms).forEach(([key, value]) => {
          if (key === 'maxAllowed') {
            if (maxAllowed !== null) {
              charms[key] = maxAllowed
            }

            return
          }

          const items = value?.items?.filter((item) => {
            if (!item?.supportedTypes || !item?.supportedTypes?.length) {
              return true
            }

            return item.supportedTypes.includes(type)
          })

          charms[key] = {
            ...value,
            items,
          }
        })

        const data = {
          charms,
          currency,
          products,
        }

        types[type] = data
      }

      resolve(types)
    })
  }

  /**
   * Get query.
   * @returns {String}
   */
  getQuery() {
    return `
      query collection(
        $after: String,
        $country: CountryCode!,
        $handle: String!,
        $language: LanguageCode!,
        $metafields: [HasMetafieldsIdentifier!]!,
      ) @inContext(country: $country, language: $language) {
        collection(handle: $handle) {
          handle
          maxAllowed: metafield(namespace: "custom", key: "max_allowed_charms") {
            value
          }
          products(first: 100, after: $after) {
            edges {
              node {
                ... on Product {
                  availableForSale
                  charmGroup: metafield(namespace: "custom", key: "charm_group") {
                    value
                  }
                  enableCharms: metafield(namespace: "custom", key: "enable_charms") {
                    value
                  }
                  descriptionHtml
                  handle
                  images(first: 1) {
                    edges {
                      node {
                        url
                      }
                    }
                  }
                  material: metafield(namespace: "custom", key: "material") {
                    reference {
                      ... on Metaobject {
                        fields {
                          key
                          value
                        }
                      }
                    }
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                    }
                  }
                  supported: metafield(namespace: "custom", key: "charm_supported_types") {
                    value
                  }
                  totalInventory
                  title
                  variants(first: 100) {
                    edges {
                      node {
                        availableForSale
                        id
                        metafields(identifiers: $metafields) {
                          key
                          value
                        }
                        price {
                          amount
                        }
                        quantityAvailable
                        sku
                        title
                      }
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
          title
        }
      }
    `
  }

  /**
   * Format charm response.
   * @param {Object} response - Charm response.
   * @returns {Object}
   */
  formatCharm(response) {
    if (!response?.node) {
      return {}
    }

    const node = response.node

    const image = node.images?.edges?.length
      ? node.images.edges[0].node.url
      : ''

    let fullImage = ''
    let thumbImage = ''

    if (image) {
      fullImage = `${image}&width=1500`
      thumbImage = `${image}&width=500`
    }

    const supportedTypes = node.supported?.value
      ? JSON.parse(node.supported?.value)
      : ''

    const title = node.title
    const price = this.formatPriceAmount(node?.priceRange?.minVariantPrice?.amount)
    const variant = node.variants?.edges[0]?.node
    const variantId = this.formatVariantId(variant?.id)

    const { availability, inventory } = this.formatAvailability({
      availability: variant.availableForSale,
      charm: true,
      inventory: node.totalInventory,
    })

    const engraving = this.formatEngraving(variant, variantId)

    /**
     * Output data.
     */
    return {
      availability,
      charmGroup: node.charmGroup?.value || 'Other',
      engraving,
      'full-image': fullImage,
      id: node.handle,
      infoHtml: {
        title,
        price,
        description: node.descriptionHtml,
        productLink: `/products/${node.handle}`,
        image: fullImage,
      },
      inventory,
      price,
      sku: variant.sku,
      supportedTypes,
      'thumb-image': thumbImage,
      title,
      variantId,
    }
  }

  /**
   * Format product response.
   * @param {Object} response - Product response.
   * @returns {Object}
   */
  formatProduct(response) {
    if (!response?.node) {
      return {}
    }

    const node = response.node

    const image = node.images?.edges?.length
      ? node.images.edges[0].node.url
      : ''

    let fullImage = ''
    let thumbImage = ''

    if (image) {
      fullImage = `${image}&width=1500`
      thumbImage = `${image}&width=500`
    }

    const name = node.title
    const material = this.formatMaterial(node.material?.reference?.fields)
    const price = this.formatPriceAmount(node?.priceRange?.minVariantPrice?.amount)
    const variant = node.variants?.edges[0]?.node
    const variantId = this.formatVariantId(variant?.id)

    const length = node.variants?.edges?.map(({node}) => {
      const { availability, inventory } = this.formatAvailability({
        availability: node.availableForSale,
        inventory: node.quantityAvailable,
      })

      return {
        availability,
        inventory,
        name: node.title,
        price: this.formatPriceAmount(node?.price?.amount),
        sku: node.sku,
        variantId: this.formatVariantId(node?.id),
      }
    })

    const { availability, inventory } = this.formatAvailability({
      availability: node.availableForSale,
      inventory: node.totalInventory,
    })

    return {
      availability,
      description: node.descriptionHtml,
      'full-image': fullImage,
      id: node.handle,
      inventory,
      length,
      material,
      name,
      price,
      selected: this.dataset.selectedProduct === node.handle,
      sku: variant.sku,
      'thumb-image': thumbImage,
      variantId,
    }
  }

  /**
   * Format availability and inventory.
   * @param {Boolean} availability - Availability.
   * @param {Boolean} [charm] - Is a charm?
   * @param {Number} inventory - Inventory.
   * @returns {Object}
   */
  formatAvailability({ availability, charm, inventory } = {}) {
    let localAvailability = availability
    let localInventory = inventory

    /**
     * If inventory is 0 but it's available then continue selling is enabled
     * or inventory isn't tracked.
     */
    if (localInventory < 1 && localAvailability) {
      localInventory = 100
    }

    /**
     * If charm inventory is less than maxAllowed set to unavailable.
     * - Request from Doogma.
     */
    if (charm && localInventory < this.maxAllowed) {
      localAvailability = false
    }

    return {
      availability: localAvailability,
      inventory: localInventory,
    }
  }

  /**
   * Format engraving data on variant.
   * @param {Object} variant - Variant response.
   * @param {Number} variantId - Variant ID.
   * @returns {Object}
   */
  formatEngraving(variant, variantId) {
    const data = {
      backEngraving: false,
      boundaryPreset: null,
      boundingHeight: null,
      boundingWidth: null,
      enabled: false,
      id: variantId,
      maxChars: null,
      price: this.dataset.engravingCost,
      productHeight: null,
      productWidth: null,
      sku: variant.sku,
      symbols: false,
    }

    variant.metafields.forEach((metafield) => {
      if (!metafield) {
        return
      }

      const string = metafield.key
        .replace('engraving_', '')
        .replaceAll('_', ' ')

      const key = this.convertToCamelCase(string)

      switch (metafield.key) {
        case 'engraving_allow_symbols':
          data.symbols = metafield.value === 'true'
          break

        case 'engraving_back_engraving':
          data.backEngraving = metafield.value === 'true'
          break

        case 'engraving_boundary_preset':
          data.boundaryPreset = Number(metafield.value)
          break

        case 'engraving_max_characters':
          data.maxChars = Number(metafield.value)
          break

        default:
          data[key] = metafield.value
          break
      }
    })

    if (data.maxChars) {
      data.enabled = data.maxChars > 0
    }

    return data
  }

  /**
   * Convert a string into camelCase.
   * @param {String} string - String to convert.
   * @returns {String}
   */
  convertToCamelCase(string) {
    if (!string) {
      return ''
    }

    return string
      .replace(
        /.\b(?<word>\w)/g,
        (_, $1) => {
          return $1.toUpperCase()
        },
      )
      .replace(/[\s:'"]/g, '')
      .replace(
        /^(?<rest>.)/u,
        (_, $1) => {
          return $1.toLowerCase()
        },
      )
  }

  /**
   * Format variant ID.
   * @param {String} id - Variant storefront API ID.
   * @returns {Number}
   */
  formatVariantId(id) {
    if (!id) {
      return 0
    }

    return Number(id.split('/')?.pop())
  }

  /**
   * Format a price amount.
   * - If the amount can be converted to a number, it is converted and multiplied
   *   by 100 to get the cents amount.
   * - Otherwise returns `false`.
   * @param {*} amount - Amount value to process.
   * @returns {Number}
   */
  formatPriceAmount(amount) {
    // eslint-disable-next-line no-undefined
    if (amount === null || amount === undefined) {
      return 0
    }

    const numericAmount = Number(amount)

    if (isNaN(numericAmount)) {
      return 0
    }

    return (Math.round(numericAmount * 100) / 100).toFixed(2)
  }

  /**
   * Format material metafield.
   * @param {Array} fields - Metaobject entry fields.
   * @returns {Object}
   */
  formatMaterial(fields) {
    if (!fields?.length) {
      return {}
    }

    const object = {}

    fields.forEach((field) => {
      object[field.key] = field.value
    })

    return object
  }

  /**
   * Generic query function.
   * - Uses cache if supported and set.
   * @param {String} query - Query.
   * @param {Object} variables - Variables.
   * @returns {Promise<Object>}
   */
  async query(query, variables) {
    return new Promise(async(resolve, reject) => {
      const store = this.dataset.store
      const version = this.dataset.version
      const key = this.dataset.key
      const url = `https://${store}.myshopify.com/api/${version}/graphql.json?cacheBust=${Date.now()}`

      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': key,
      }

      let response = {}

      try {
        const storageKey = this.getStorageKey(variables)

        if (storageKey) {
          const cache = sessionStorage.getItem(storageKey)

          if (cache) {
            resolve(JSON.parse(cache))
            return
          }
        }

        response = await fetch(url, {
          body: JSON.stringify({
            query,
            variables,
          }),
          headers,
          method: 'POST',
        })

        if (!response.ok) {
          reject(response.description)
          return
        }

        response = await response.json()

        if (storageKey) {
          sessionStorage.setItem(storageKey, JSON.stringify(response))
        }

        resolve(response)

      } catch (error) {
        reject(error)
        return
      }
    })
  }

  /**
   * Get storage key for sessionStorage caching.
   * @param {Object} variables - GraphQL variables.
   * @returns {String}
   */
  getStorageKey(variables = {}) {
    if (!this.queryCachePrefix || !this.isStorageSupported()) {
      return ''
    }

    const variableString = Object.entries(variables)
      .map(([key, value]) => {
        if (
          ['country', 'language', 'metafields'].includes(key) ||
          value === null
        ) {
          return false
        }

        return `${key}:${JSON.stringify(value)}`
      })
      .filter(Boolean)
      .join('-')

    return `${this.queryCachePrefix}-${variableString}`
  }

  /**
   * Determine if sessionStorage is supported.
   * - Adapted from https://mmazzarolo.com/blog/2022-06-25-local-storage-status/
   * @returns {Boolean}
   */
  isStorageSupported(storageType = 'sessionStorage') {
    let storage = false

    try {
      storage = window[storageType]

      if (!storage) {
        return false
      }

      const test = `__storage_test__`

      storage.setItem(test, test)
      storage.removeItem(test)
      return true

    } catch (error) {

      /**
       * We acknowledge a QuotaExceededError only if there's something stored.
       */
      const isValidQuotaExceededError = isQuotaExceededError(error) && storage.length
      return isValidQuotaExceededError
    }
  }

  /**
   * Determine whether a storage error is a QuotaExceededError.
   * - I.e. there isn't enough room to store the data.
   * @param {*} error - Error to check.
   * @returns {Boolean}
   */
  isQuotaExceededError(error) {
    return (
      error instanceof DOMException &&
      (
        [22, 1014].includes(error.code) ||
        ['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED'].includes(error.name)
      )
    )
  }

  /**
   * Update window variables.
   */
  updateWindow() {
    Object.entries(this.types).forEach(([key, value]) => {
      window[`doogma${key}`] = value
    })

    /**
     * Write complete charms data for use in add to cart.
     */
    const charms = []

    Object.entries(this.charms).forEach(([key, value]) => {
      if (key === 'maxAllowed' || !value?.items?.length) {
        return
      }

      charms.push(...value.items)
    })

    window.doogmaCharms = charms
  }

  /**
   * Emit button state.
   * @param {String} state - 'loading' or 'ready'.
   */
  setButtonState(state) {
    if (state === 'loading') {
      var event = new Event('doogmaButtonLoading')
      document.dispatchEvent(event)
      return
    }

    var event = new Event('doogmaButtonReady')
    document.dispatchEvent(event)

    /**
     * Set item as button loads after charms when using cache.
     */
    sessionStorage.setItem('doogma-ready', JSON.stringify(true))
  }
}

customElements.define('doogma-charms', DoogmaCharms)
